/**
 * WebM recorder (SPEC §6.3).
 *
 * Wraps `MediaRecorder` to capture an `HTMLCanvasElement` at 60fps, VP9,
 * 12 Mbps. The recorder is intentionally agnostic about *which* canvas
 * it is handed.
 *
 * IMPORTANT — text overlay during WebM recording:
 *   `MediaRecorder` only sees the bitstream of the canvas it captures.
 *   The DOM-level typography overlay is therefore NOT recorded. Wave 4
 *   (`app.ts`) is responsible for ensuring that, while recording is
 *   active, the canvas passed to {@link createWebmRecorder} contains a
 *   composited frame (WebGL render + typography). The simplest pattern:
 *     - Maintain a single visible canvas which, during recording, has
 *       the typography drawn into it each frame via a 2D context (or by
 *       compositing a typography canvas via `drawImage`).
 *     - Or pass a dedicated "composite" canvas (kept in sync with the
 *       WebGL output + text) to this recorder while leaving the live
 *       view untouched.
 *   This module does not attempt to enforce either strategy — it only
 *   records whatever frames flow through the canvas it is given.
 *
 * No `Math.random()`. No new dependencies — `MediaRecorder` is a
 * browser-native API.
 */

import type { AspectId, ChannelId } from '../types.js';

export interface WebmRecorderOpts {
  /** The canvas to record. Must produce frames during the recording window. */
  canvas: HTMLCanvasElement;
  /** Frame rate for the captured stream. Default 60. */
  fps?: number;
  /** Target bitrate. Default 12 Mbps. */
  bitsPerSecond?: number;
  /** Container/codec mime type. If omitted the recorder picks the
   *  first supported entry from {@link MIME_CANDIDATES}. */
  mimeType?: string;
  /**
   * Audio streams to mix into the recording. Each stream's audio
   * tracks are added to the recorded `MediaStream`; video tracks on
   * these inputs are ignored (the canvas is the only video source).
   * Empty / undefined = silent recording (legacy behaviour).
   */
  audioStreams?: MediaStream[];
}

export interface WebmStopResult {
  blob: Blob;
  filename: string;
  durationSeconds: number;
}

export interface WebmRecorder {
  start(meta: { aspect: AspectId; channel: ChannelId; seed: string }): void;
  stop(): Promise<WebmStopResult>;
  isRecording(): boolean;
  elapsedSeconds(): number;
}

/**
 * SPEC §6.3 filename:
 *   `kanawatsugi_<aspect>_<channel>_<seed>_<duration>s.webm`
 * Duration is rounded to the nearest integer second.
 */
export function buildWebmFilename(
  aspect: AspectId,
  channel: ChannelId,
  seed: string,
  durationSeconds: number,
): string {
  const safe = Number.isFinite(durationSeconds) && durationSeconds > 0 ? durationSeconds : 0;
  const seconds = Math.max(0, Math.round(safe));
  return `kanawatsugi_${aspect}_${channel}_${seed}_${seconds}s.webm`;
}

/**
 * Re-export the download helper from `png.ts` so callers can do a
 * single `import { downloadBlob } from './capture/webm.js'` for both
 * export pipelines if they prefer.
 */
export { downloadBlob } from './png.js';

const DEFAULT_FPS = 60;
const DEFAULT_BITRATE = 12_000_000;

/**
 * Codec preference order. The opus-paired entries come first so a
 * recording with audio inputs lands in a single muxed file; the
 * audio-less entries remain at the tail for back-compat with older
 * Chromium builds and for callers that pass no `audioStreams`.
 *
 * iPad / iOS Safari does not implement VP9 in MediaRecorder and is
 * intentionally not handled here — the caller surfaces the resulting
 * "codec not supported" error to the user (展示 iPad では録画機能を
 * 使わない前提).
 */
const MIME_CANDIDATES = [
  'video/webm;codecs=vp9,opus',
  'video/webm;codecs=vp8,opus',
  'video/webm;codecs=vp9',
  'video/webm;codecs=vp8',
  'video/webm',
] as const;
const DEFAULT_MIME = MIME_CANDIDATES[0];

/**
 * Type guard for the global `MediaRecorder` constructor. We avoid a
 * top-level reference so that this module type-checks in non-DOM
 * contexts (e.g. SSR, isolated tests).
 */
function getMediaRecorderCtor(): typeof MediaRecorder | null {
  if (typeof globalThis === 'undefined') return null;
  const g = globalThis as unknown as { MediaRecorder?: typeof MediaRecorder };
  return typeof g.MediaRecorder === 'function' ? g.MediaRecorder : null;
}

/**
 * Probe codec support. We require the configured mime type to be
 * supported — silently downgrading would be a quality regression
 * (SPEC quality bar).
 */
function isMimeSupported(MR: typeof MediaRecorder, mimeType: string): boolean {
  if (typeof MR.isTypeSupported !== 'function') {
    // Older browsers — best-effort attempt at construction time will
    // surface failure to the caller via `start()`.
    return true;
  }
  return MR.isTypeSupported(mimeType);
}

/**
 * Pick the highest-priority supported mime from {@link MIME_CANDIDATES},
 * preferring opus-paired entries so recordings with audio inputs end
 * up muxed into one file. Returns `null` if the browser supports none
 * of them — the caller surfaces a clear error to the user.
 */
function pickSupportedMime(MR: typeof MediaRecorder): string | null {
  if (typeof MR.isTypeSupported !== 'function') {
    return DEFAULT_MIME;
  }
  // Debug log: dump every candidate's support status so a silent /
  // codec-less recording can be diagnosed from the console alone.
  // Remove with `[record-debug]` grep when verification is complete.
  console.log(
    '[record-debug] mime support:',
    MIME_CANDIDATES.map((m) => `${m}: ${MR.isTypeSupported(m)}`).join(' | '),
  );
  for (const candidate of MIME_CANDIDATES) {
    if (MR.isTypeSupported(candidate)) return candidate;
  }
  return null;
}

export function createWebmRecorder(opts: WebmRecorderOpts): WebmRecorder {
  const fps = opts.fps ?? DEFAULT_FPS;
  const bitsPerSecond = opts.bitsPerSecond ?? DEFAULT_BITRATE;
  // If the caller passed an explicit mime, honour it (and fail loudly
  // below if unsupported). Otherwise negotiate against the candidate
  // list so an opus-paired codec wins when audio inputs are mixed in.
  const audioStreams = opts.audioStreams ?? [];

  let recorder: MediaRecorder | null = null;
  let stream: MediaStream | null = null;
  let chunks: Blob[] = [];
  let startTimeMs = 0;
  let stoppedAtMs = 0;
  let active = false;
  let meta: { aspect: AspectId; channel: ChannelId; seed: string } | null = null;
  let stopPromise: Promise<WebmStopResult> | null = null;
  /** Mime negotiated for the active session (used by the final Blob too). */
  let activeMime: string = opts.mimeType ?? DEFAULT_MIME;

  function nowMs(): number {
    if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
      return performance.now();
    }
    return Date.now();
  }

  return {
    start(metaIn): void {
      if (active) {
        throw new Error('WebM recorder: start() called while already recording.');
      }

      const MR = getMediaRecorderCtor();
      if (MR === null) {
        throw new Error(
          'WebM recorder: MediaRecorder API is not available in this environment.',
        );
      }
      // Mime negotiation: explicit `opts.mimeType` is honoured as-is
      // (and must be supported); otherwise we walk the candidate list
      // to land on the best opus-paired codec available.
      if (opts.mimeType !== undefined) {
        if (!isMimeSupported(MR, opts.mimeType)) {
          throw new Error(
            `WebM recorder: codec "${opts.mimeType}" is not supported by this browser.`,
          );
        }
        activeMime = opts.mimeType;
      } else {
        const negotiated = pickSupportedMime(MR);
        if (negotiated === null) {
          throw new Error(
            'WebM recorder: no supported WebM codec found ' +
              '(tried vp9/opus, vp8/opus, vp9, vp8, webm).',
          );
        }
        activeMime = negotiated;
      }
      const captureStream = (
        opts.canvas as HTMLCanvasElement & { captureStream?: (fps?: number) => MediaStream }
      ).captureStream;
      if (typeof captureStream !== 'function') {
        throw new Error(
          'WebM recorder: HTMLCanvasElement.captureStream is not available in this browser.',
        );
      }

      const videoStream = captureStream.call(opts.canvas, fps);
      // Build a fresh recording stream that carries the canvas's video
      // tracks plus every audio track collected from `audioStreams`.
      // Video tracks on the audio inputs are intentionally skipped:
      // any background-video frames are already composited onto the
      // canvas, so re-adding them would double-encode the picture.
      const recordStream = new MediaStream();
      videoStream.getVideoTracks().forEach((t) => recordStream.addTrack(t));
      for (const s of audioStreams) {
        s.getAudioTracks().forEach((t) => recordStream.addTrack(t));
      }
      stream = recordStream;

      // Debug log: video / audio track counts on the final stream
      // and the mimeType the recorder was actually instantiated with.
      // If audio=0 here, the mux will of course be silent regardless
      // of how the upstream Tone tap is wired. Remove with
      // `[record-debug]` grep when verification is complete.
      console.log(
        '[record-debug] recordStream tracks:',
        'video=' + recordStream.getVideoTracks().length,
        'audio=' + recordStream.getAudioTracks().length,
        'mime=' + activeMime,
      );

      let mr: MediaRecorder;
      try {
        mr = new MR(stream, { mimeType: activeMime, videoBitsPerSecond: bitsPerSecond });
      } catch (err) {
        // Clean up canvas capture stream on failure (recordStream's
        // tracks are aliases of videoStream's, so stopping once is
        // enough).
        videoStream.getTracks().forEach((t) => t.stop());
        stream = null;
        throw new Error(
          `WebM recorder: failed to construct MediaRecorder (${(err as Error).message}).`,
        );
      }

      chunks = [];
      mr.ondataavailable = (ev: BlobEvent): void => {
        if (ev.data && ev.data.size > 0) {
          chunks.push(ev.data);
        }
      };
      // We don't attach onstop here; stop() awaits it via a fresh promise.

      recorder = mr;
      meta = metaIn;
      startTimeMs = nowMs();
      stoppedAtMs = 0;
      stopPromise = null;
      active = true;

      // Emit a chunk roughly every second so a long recording doesn't
      // accumulate everything in one final blob (also helps surface
      // streaming failures earlier).
      mr.start(1000);
    },

    stop(): Promise<WebmStopResult> {
      if (stopPromise !== null) {
        return stopPromise;
      }
      if (!active || recorder === null || meta === null) {
        return Promise.reject(new Error('WebM recorder: stop() called while not recording.'));
      }
      const mr = recorder;
      const capturedMeta = meta;

      stopPromise = new Promise<WebmStopResult>((resolve, reject) => {
        mr.onstop = (): void => {
          try {
            stoppedAtMs = nowMs();
            const durationSeconds = Math.max(0, (stoppedAtMs - startTimeMs) / 1000);
            const blob = new Blob(chunks, { type: activeMime });
            const filename = buildWebmFilename(
              capturedMeta.aspect,
              capturedMeta.channel,
              capturedMeta.seed,
              durationSeconds,
            );

            // Release the capture stream tracks now that recording is done.
            if (stream !== null) {
              stream.getTracks().forEach((t) => t.stop());
            }

            // Reset state so a subsequent start() works cleanly.
            active = false;
            recorder = null;
            stream = null;
            chunks = [];
            meta = null;

            resolve({ blob, filename, durationSeconds });
          } catch (err) {
            reject(err as Error);
          }
        };
        mr.onerror = (ev: Event): void => {
          reject(
            new Error(
              `WebM recorder: MediaRecorder error (${(ev as unknown as { error?: { message?: string } }).error?.message ?? 'unknown'}).`,
            ),
          );
        };
        try {
          // requestData triggers a final ondataavailable before stop fires.
          if (typeof mr.requestData === 'function' && mr.state === 'recording') {
            mr.requestData();
          }
          mr.stop();
        } catch (err) {
          reject(err as Error);
        }
      });

      return stopPromise;
    },

    isRecording(): boolean {
      return active;
    },

    elapsedSeconds(): number {
      if (!active) return 0;
      return (nowMs() - startTimeMs) / 1000;
    },
  };
}
